/**
 * useIterationProgress Hook
 *
 * Fetches initial progress via REST API, then connects to WebSocket for updates.
 * Implements self-ticking elapsed timer based on server's started_at timestamp.
 *
 * Flow:
 * 1. Fetch initial progress from GET /api/iterations/{taskId}/iterations/{iterationId}/progress
 * 2. Initialize elapsed timer from server's started_at
 * 3. Connect to WebSocket for real-time updates
 * 4. Self-tick elapsed timer locally (every second)
 * 5. On WebSocket update, refresh progress data but keep timer ticking
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { IterationProgress, ProgressWebSocketMessage } from '@/types/tasks';
import api from '@/services/api';

// Get WebSocket URL from environment or construct from current location
function getWebSocketUrl(iterationId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws/progress/${iterationId}`;
}

export interface UseIterationProgressOptions {
  /** Task ID for the initial API call */
  taskId: string;
  /** Enable automatic reconnection on disconnect */
  autoReconnect?: boolean;
  /** Maximum number of reconnection attempts */
  maxReconnectAttempts?: number;
  /** Reconnection delay in ms (doubles on each attempt) */
  reconnectDelay?: number;
  /** Ping interval in ms to keep connection alive */
  pingInterval?: number;
  /** Timer tick interval in ms (default 1000ms) */
  timerInterval?: number;
  /** Enable debug logging */
  debug?: boolean;
}

export interface UseIterationProgressReturn {
  /** Current progress state with locally-ticked elapsed time */
  progress: IterationProgress | null;
  /** Locally calculated elapsed seconds (ticks every second) */
  elapsedSeconds: number | null;
  /** WebSocket connection state */
  isConnected: boolean;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Error message if connection/fetch failed */
  error: string | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** Manually refresh progress from API */
  refresh: () => Promise<void>;
}

const DEFAULT_OPTIONS = {
  autoReconnect: true,
  maxReconnectAttempts: 5,
  reconnectDelay: 1000,
  pingInterval: 30000,
  timerInterval: 1000,
  debug: false,
};

export function useIterationProgress(
  iterationId: string | null,
  options: UseIterationProgressOptions
): UseIterationProgressReturn {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const [progress, setProgress] = useState<IterationProgress | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<Date | null>(null);
  const mountedRef = useRef(true);

  const log = useCallback(
    (...args: unknown[]) => {
      if (opts.debug) {
        console.log('[useIterationProgress]', ...args);
      }
    },
    [opts.debug]
  );

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // Start the self-ticking timer based on started_at
  const startTimer = useCallback((startedAt: string | null) => {
    // Clear existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (!startedAt) {
      startedAtRef.current = null;
      setElapsedSeconds(null);
      return;
    }

    // Parse server's started_at timestamp
    const serverStartTime = new Date(startedAt);
    startedAtRef.current = serverStartTime;

    // Calculate initial elapsed
    const now = new Date();
    const initialElapsed = Math.max(0, (now.getTime() - serverStartTime.getTime()) / 1000);
    setElapsedSeconds(initialElapsed);

    log('Starting timer from', startedAt, 'initial elapsed:', initialElapsed);

    // Tick every second
    timerIntervalRef.current = setInterval(() => {
      if (startedAtRef.current && mountedRef.current) {
        const now = new Date();
        const elapsed = Math.max(0, (now.getTime() - startedAtRef.current.getTime()) / 1000);
        setElapsedSeconds(elapsed);
      }
    }, opts.timerInterval);
  }, [log, opts.timerInterval]);

  // Fetch initial progress from REST API
  const fetchProgress = useCallback(async () => {
    if (!iterationId || !opts.taskId) return;

    setIsLoading(true);
    setError(null);

    try {
      log('Fetching initial progress for', iterationId);
      const response = await api.get<IterationProgress>(
        `/tasks/${opts.taskId}/iterations/${iterationId}/progress`
      );

      if (mountedRef.current) {
        const data = response.data;
        setProgress(data);

        // Start self-ticking timer from server's started_at
        startTimer(data.started_at);

        log('Initial progress fetched:', data.progress_percent, '%');
      }
    } catch (err) {
      log('Failed to fetch progress:', err);
      if (mountedRef.current) {
        setError('Failed to fetch progress');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [iterationId, opts.taskId, log, startTimer]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    clearTimers();

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    if (mountedRef.current) {
      setIsConnected(false);
    }
  }, [clearTimers]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!iterationId) {
      log('No iteration ID provided, skipping WebSocket connection');
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const url = getWebSocketUrl(iterationId);
    log('Connecting to WebSocket', url);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        log('WebSocket connected');
        if (mountedRef.current) {
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
        }

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, opts.pingInterval);
      };

      ws.onmessage = (event) => {
        try {
          const message: ProgressWebSocketMessage = JSON.parse(event.data);
          log('WebSocket message:', message.type);

          switch (message.type) {
            case 'progress_update':
              if (message.status && mountedRef.current) {
                setProgress(message.status);
                // Update timer if started_at changed (shouldn't normally happen)
                if (message.status.started_at &&
                    message.status.started_at !== startedAtRef.current?.toISOString()) {
                  startTimer(message.status.started_at);
                }
              }
              break;

            case 'connected':
              log('Server confirmed connection');
              break;

            case 'pong':
              log('Received pong');
              break;

            case 'error':
              log('Server error:', message.message);
              if (mountedRef.current) {
                setError(message.message || 'Server error');
              }
              break;
          }
        } catch (e) {
          log('Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = () => {
        log('WebSocket error');
        if (mountedRef.current) {
          setError('WebSocket connection error');
        }
      };

      ws.onclose = (event) => {
        log('WebSocket closed:', event.code, event.reason);
        if (mountedRef.current) {
          setIsConnected(false);
        }

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Auto reconnect
        if (
          opts.autoReconnect &&
          mountedRef.current &&
          reconnectAttemptsRef.current < opts.maxReconnectAttempts
        ) {
          const delay = opts.reconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= opts.maxReconnectAttempts) {
          if (mountedRef.current) {
            setError('Maximum reconnection attempts reached');
          }
        }
      };
    } catch (e) {
      log('Failed to create WebSocket:', e);
      if (mountedRef.current) {
        setError('Failed to create WebSocket connection');
      }
    }
  }, [iterationId, opts.autoReconnect, opts.maxReconnectAttempts, opts.reconnectDelay, opts.pingInterval, log, startTimer]);

  // Reconnect handler
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    connect();
  }, [connect, disconnect]);

  // Initialize on mount / iteration change
  useEffect(() => {
    mountedRef.current = true;

    if (iterationId && opts.taskId) {
      // Reset state
      setProgress(null);
      setElapsedSeconds(null);
      setError(null);
      startedAtRef.current = null;

      // Fetch initial progress, then connect WebSocket
      fetchProgress().then(() => {
        if (mountedRef.current) {
          connect();
        }
      });
    } else {
      disconnect();
    }

    return () => {
      mountedRef.current = false;
      clearTimers();
      disconnect();
    };
  }, [iterationId, opts.taskId, fetchProgress, connect, disconnect, clearTimers]);

  return {
    progress,
    elapsedSeconds,
    isConnected,
    isLoading,
    error,
    reconnect,
    disconnect,
    refresh: fetchProgress,
  };
}

export default useIterationProgress;
